import { useState } from "react";
import { Anchor, ArrowLeftRight, Building2, Check, ChevronDown, ChevronUp, Gem, Landmark, Ship, Swords, Target } from "lucide-react";
import { Bar, PANEL, Pill, Ring } from "./kit";
import { ACC, STOPS, fmt } from "./util";

const ICONS = [Anchor, Building2, Gem, Landmark];
const COLORS = ["blue", "green", "rose", "gold"];

// The one thing to do next: [sentence (for the tooltip), [tab, section], progress 0..1, short tag].
function goalOf(e, stop) {
  if (stop === 0) return e.uxd >= e.line ? [`Rent your lot uptown: ${fmt(e.line)} UXD a week`, ["economy", "uptown"], 1, "Rent it"] : [`${fmt(e.line - e.uxd)} UXD to a week uptown`, ["economy", "uptown"], e.uxd / e.line, `${fmt(e.line - e.uxd)} to go`];
  if (stop === 1) {
    const w = e.week;
    return w.rank && w.rank <= w.seats ? ["Top 10 this week. Hold your seat until Monday", ["leaders", "week"], 1, "Top 10"]
      : [`Earn ${fmt(Math.max(0, w.row - w.earned) + 1)} more this week to reach the Row`, ["leaders", "week"], w.row ? w.earned / w.row : 0, `${fmt(Math.max(0, w.row - w.earned) + 1)} to go`];
  }
  if (stop === 2) {
    return e.hall.running ? ["You're in the race for mayor", ["hall"], 1, "Running"]
      : [`Run for mayor: ${fmt(e.costs.mayor)} UXD${e.uxd < e.costs.mayor ? ` (${fmt(e.costs.mayor - e.uxd)} to go)` : ""}`, ["hall"], Math.min(1, e.uxd / e.costs.mayor), `${fmt(e.costs.mayor)} to run`];
  }
  return [e.hall.decree_used ? "You're the mayor. Your decree is out" : "You're the mayor. Pass a decree", ["hall"], e.hall.decree_used ? 1 : 0, e.hall.decree_used ? "Decree out" : "Decree"];
}

export function GoalTracker({ econ, stop, collected, hidden, onOpen }) {
  const [open, setOpen] = useState(() => window.innerWidth >= 640);
  if (hidden) return null;
  const [text, go, value, tag] = goalOf(econ, stop);
  const went = econ.visits.of - econ.visits.left;
  // Daily tasks: icon, tooltip, progress (n of m), where it leads.
  const tasks = [
    [Ship, "Collect a ship", collected ? 1 : 0, 1, "blue", ["economy", "dock"]],
    [ArrowLeftRight, "Run 3 trades", Math.min(3, went), 3, "green", ["economy", "dock"]],
    [Swords, "Play a duel", econ.duels.free === false ? 1 : 0, 1, "rose", ["economy", "duels"]],
  ];
  const done = tasks.filter((t) => t[2] >= t[3]).length;
  const StopIcon = ICONS[stop];

  return (
    <div className={`${PANEL} pop pointer-events-auto absolute left-2 top-[128px] z-10 w-[min(86vw,300px)] rounded-[20px] p-3 sm:left-4`} data-testid="goal-tracker">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left" aria-expanded={open} data-testid="goal-toggle" title={open ? "Hide goals" : text}>
        <Target className="h-4 w-4 shrink-0 text-x-mute" strokeWidth={2.2} />
        {open ? <span className="flex-1 text-sm font-semibold">Your goal</span> : (
          <>
            <StopIcon className="acc-text h-4 w-4 shrink-0" style={{ "--acc": ACC[COLORS[stop]] }} strokeWidth={2.2} />
            <span className="min-w-0 flex-1"><Bar value={value} color={ACC[COLORS[stop]]} /></span>
            <Pill acc={COLORS[stop]}>{tag}</Pill>
            <span className="text-[11px] font-semibold tabular-nums text-x-mute">{done}/3</span>
          </>
        )}
        {open ? <ChevronUp className="h-4 w-4 text-x-mute" /> : <ChevronDown className="h-4 w-4 text-x-mute" />}
      </button>
      {open && (
        <>
          <ol className="relative mt-3 grid grid-cols-4">
            <span className="hud-well absolute left-[12.5%] right-[12.5%] top-4 h-1.5 -translate-y-1/2 rounded-full" />
            <span className="absolute left-[12.5%] top-4 h-1.5 -translate-y-1/2 rounded-full transition-[width] duration-500" style={{ width: `${stop * 25}%`, background: ACC[COLORS[stop]] }} />
            {STOPS.map((s, i) => {
              const Icon = ICONS[i], c = ACC[COLORS[i]];
              return (
                <li key={s} className="relative flex flex-col items-center gap-1" data-current={i === stop} style={{ "--acc": c }}>
                  <span className={`grid h-8 w-8 place-items-center rounded-full ${i <= stop ? "acc-fill" : "bg-x-surface text-x-mute"}`} style={i === stop ? { outline: `2px solid ${c}`, outlineOffset: 2 } : undefined}>
                    <Icon className="h-4 w-4" strokeWidth={2.2} />
                  </span>
                  <span className={`text-[11px] font-semibold ${i > stop ? "text-x-mute" : ""}`}>{s}</span>
                </li>
              );
            })}
          </ol>
          <button type="button" onClick={() => onOpen(...go)} data-testid="goal-next" title={text}
            className="acc-card lift mt-3 flex w-full items-center gap-2 rounded-[10px] px-3 py-2" style={{ "--acc": ACC[COLORS[stop]] }}>
            <span className="sr-only">{text}</span>
            <StopIcon className="h-5 w-5 shrink-0" strokeWidth={2.2} />
            <span className="min-w-0 flex-1"><Bar value={value} color={ACC[COLORS[stop]]} /></span>
            <Pill acc={COLORS[stop]} className="!text-xs">{tag}</Pill>
          </button>
          <ul className="mt-3 grid grid-cols-3 gap-2">
            {tasks.map(([Icon, label, n, of, acc, to]) => {
              const ok = n >= of;
              return (
                <li key={label}>
                  <button type="button" onClick={() => onOpen(...to)} title={`${label}${of > 1 ? ` · ${n}/${of}` : ""}`} aria-label={label} data-done={ok}
                    className="lift flex w-full flex-col items-center gap-1 rounded-[10px] py-1.5" style={{ "--acc": ACC[acc] }}>
                    <Ring value={n / of} size={42} stroke={4} acc={acc}>
                      {ok ? <Check className="badge-pop h-5 w-5" strokeWidth={4} style={{ color: ACC[acc] }} /> : <Icon className="h-[18px] w-[18px]" strokeWidth={2.5} style={{ color: ACC[acc] }} />}
                    </Ring>
                    <span className={`text-[11px] font-semibold tabular-nums ${ok ? "acc-text" : "text-x-mute"}`}>{of > 1 ? `${n}/${of}` : ok ? "Done" : "–"}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
